import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useTheme } from './hooks/useTheme';
import { routerConfig } from './router-config';
import Layout from './components/Layout/Layout';
import Landing from './pages/Landing';
import Login from './pages/Auth/Login';
import Signup from './pages/Auth/Signup';
import Dashboard from './pages/Dashboard';
import Notes from './pages/Notes/Notes';
import NoteEditor from './pages/Notes/NoteEditor';
import MarkdownWiki from './pages/Notes/MarkdownWiki';
import Projects from './pages/Projects/Projects';
import ProjectEditor from './pages/Projects/ProjectEditor';
import Graph from './pages/Graph/Graph';
import Documents from './pages/Documents/Documents';
import DocumentUpload from './pages/Documents/DocumentUpload';
import GoogleDocsIntegration from './pages/Documents/GoogleDocsIntegration';
import Calendar from './pages/Calendar/Calendar';
import AcademicCalendar from './pages/Calendar/AcademicCalendar';
import TaskBoard from './pages/Tasks/TaskBoard';
import SimpleTodo from './pages/Todo/SimpleTodo';
import Chat from './pages/Chat/Chat';
import Profile from './pages/Profile/Profile';
import Settings from './pages/Settings/Settings';
import Subscription from './pages/Subscription/Subscription';

// Loading component with debug info
const LoadingScreen = () => {
  const { colors } = useTheme();
  const [loadingTime, setLoadingTime] = React.useState(0);

  // Update loading time every second
  React.useEffect(() => {
    const interval = setInterval(() => {
      setLoadingTime(prev => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center transition-colors duration-300 space-y-4"
      style={{ backgroundColor: colors.base, color: colors.text }}
    >
      <div className="text-2xl font-semibold">Loading EnkiAI...</div>
      <div className="text-sm opacity-70">Initializing application</div>
      <div className="text-xs opacity-50">Loading time: {loadingTime}s</div>
      {import.meta.env.DEV && (
        <div className="text-xs opacity-50 mt-8 space-y-1">
          <div>Debug Info:</div>
          <div>Auth Token: {localStorage.getItem('sb-auth-token') ? 'Present' : 'Missing'}</div>
          <div>Theme: {localStorage.getItem('enki-theme') || 'Not set'}</div>
          <div>URL: {window.location.href}</div>
          <div>Session: {localStorage.getItem('sb-auth-token') ? JSON.stringify(JSON.parse(localStorage.getItem('sb-auth-token') || '{}'), null, 2) : 'No session'}</div>
        </div>
      )}
      {loadingTime > 10 && (
        <div className="text-sm text-red-500 mt-4">
          Loading is taking longer than usual. You may want to refresh the page.
        </div>
      )}
    </div>
  );
};

// Error boundary component
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('App Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
          <pre className="text-sm bg-red-100 p-4 rounded max-w-2xl overflow-auto">
            {this.state.error?.toString()}
          </pre>
          <button
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => window.location.reload()}
          >
            Reload App
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, error } = useAuth();
  const { colors } = useTheme();
  
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div 
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: colors.base, color: colors.text }}
      >
        <div className="text-xl font-semibold mb-4">Authentication Error</div>
        <div className="text-sm opacity-70 mb-4">{error}</div>
        <button
          className="px-4 py-2 rounded"
          style={{ backgroundColor: colors.blue, color: 'white' }}
          onClick={() => window.location.href = '/login'}
        >
          Return to Login
        </button>
      </div>
    );
  }
  
  return user ? <>{children}</> : <Navigate to="/login" />;
};

// Public Route Component (redirects to dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isLoading, error } = useAuth();
  
  if (isLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <Navigate to="/login" />;
  }
  
  return user ? <Navigate to="/dashboard" /> : <>{children}</>;
};

const AppContent: React.FC = () => {
  const { colors } = useTheme();

  // Apply theme colors to document root and body on every render
  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    
    // Apply CSS variables
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
    
    // Apply body styles
    body.style.setProperty('background-color', colors.base, 'important');
    body.style.setProperty('color', colors.text, 'important');
    body.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease', 'important');
    
    // Apply to html element
    root.style.setProperty('background-color', colors.base, 'important');
    root.style.setProperty('color', colors.text, 'important');
    root.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease', 'important');
    
    // Apply to root div
    const rootDiv = document.getElementById('root');
    if (rootDiv) {
      rootDiv.style.setProperty('background-color', colors.base, 'important');
      rootDiv.style.setProperty('color', colors.text, 'important');
      rootDiv.style.setProperty('transition', 'background-color 0.3s ease, color 0.3s ease', 'important');
      rootDiv.style.setProperty('min-height', '100vh', 'important');
    }
  }, [colors]);

  return (
    <div 
      className="min-h-screen transition-colors duration-300" 
      style={{ backgroundColor: colors.base, color: colors.text }}
    >
      <ErrorBoundary>
        <Suspense fallback={<LoadingScreen />}>
          <Router future={routerConfig.future}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={
                <PublicRoute>
                  <Landing />
                </PublicRoute>
              } />
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              <Route path="/signup" element={
                <PublicRoute>
                  <Signup />
                </PublicRoute>
              } />

              {/* Protected routes */}
              <Route path="/*" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="notes" element={<Notes />} />
                <Route path="notes/new" element={<NoteEditor />} />
                <Route path="notes/wiki" element={<MarkdownWiki />} />
                <Route path="notes/:id" element={<NoteEditor />} />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/new" element={<ProjectEditor />} />
                <Route path="projects/:id" element={<ProjectEditor />} />
                <Route path="graph" element={<Graph />} />
                <Route path="documents" element={<Documents />} />
                <Route path="documents/upload" element={<DocumentUpload />} />
                <Route path="documents/google-drive" element={<GoogleDocsIntegration />} />
                <Route path="calendar" element={<Calendar />} />
                <Route path="academic-calendar" element={<AcademicCalendar />} />
                <Route path="task-board" element={<TaskBoard />} />
                <Route path="simple-todo" element={<SimpleTodo />} />
                <Route path="chat" element={<Chat />} />
                <Route path="subscription" element={<Subscription />} />
                <Route path="profile" element={<Profile />} />
                <Route path="settings" element={<Settings />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Router>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;